/**
 * The keyboard shortcuts the editor responds to, for DISPLAY: the toolbar
 * tooltips and the order sheet's Shortcuts dialog both read this list.
 *
 * The bindings themselves live elsewhere - Tiptap's own extensions,
 * WordShortcuts.js and PageBreak.js - so a key added or changed there has to
 * be mirrored here, or the tooltip will advertise the wrong one.
 *
 * Item ids that match a toolbar name (bold, alignleft, pagebreak...) are what
 * Toolbar.js uses to append the shortcut to that button's tooltip.
 *
 * Bindings use ProseMirror's notation, where `Mod` is Cmd on macOS and Ctrl
 * everywhere else.
 */

const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || "");

const KEY_LABELS = {
  Mod: IS_MAC ? "⌘" : "Ctrl",
  Alt: IS_MAC ? "⌥" : "Alt",
  Shift: "Shift",
  ArrowUp: "↑",
  ArrowDown: "↓",
  PageUp: "Page Up",
  PageDown: "Page Down",
};

export const KEYBOARD_SHORTCUTS = [
  {
    group: "General",
    items: [
      { id: "save", label: "Save draft", keys: ["Mod-s"] },
      { id: "print", label: "Print order sheet", keys: ["Mod-p"] },
      { id: "undo", label: "Undo", keys: ["Mod-z"] },
      { id: "redo", label: "Redo", keys: ["Mod-y", "Mod-Shift-z"] },
    ],
  },
  {
    group: "Text",
    items: [
      { id: "bold", label: "Bold", keys: ["Mod-b"] },
      { id: "italic", label: "Italic", keys: ["Mod-i"] },
      { id: "underline", label: "Underline", keys: ["Mod-u"] },
      { id: "strikethrough", label: "Strikethrough", keys: ["Mod-Shift-s"] },
      { id: "bullist", label: "Bulleted list", keys: ["Mod-Shift-8"] },
      { id: "numlist", label: "Numbered list", keys: ["Mod-Shift-7"] },
      { id: "blockquote", label: "Blockquote", keys: ["Mod-Shift-b"] },
    ],
  },
  {
    group: "Paragraph",
    items: [
      { id: "alignleft", label: "Align left", keys: ["Mod-l"] },
      { id: "aligncenter", label: "Align centre", keys: ["Mod-e"] },
      { id: "alignright", label: "Align right", keys: ["Mod-r"] },
      { id: "alignjustify", label: "Justify", keys: ["Mod-j"] },
      { id: "indent", label: "Increase indent", keys: ["Mod-m"] },
      { id: "outdent", label: "Decrease indent", keys: ["Mod-Shift-m"] },
      { id: "linebreak", label: "Line break, same paragraph", keys: ["Shift-Enter"] },
      { id: "pagebreak", label: "Page break", keys: ["Mod-Enter"] },
    ],
  },
  {
    group: "Tables",
    items: [
      {
        id: "tablefromruler",
        label: "Create a table, one column per +---+",
        typed: "+---+---+",
        keys: ["Enter"],
      },
      { id: "nextcell", label: "Next cell (adds a row in the last cell)", keys: ["Tab"] },
      { id: "previouscell", label: "Previous cell", keys: ["Shift-Tab"] },
      { id: "firstcellinrow", label: "First cell in row", keys: ["Alt-Home"] },
      { id: "lastcellinrow", label: "Last cell in row", keys: ["Alt-End"] },
      { id: "topcellincolumn", label: "Top cell in column", keys: ["Alt-PageUp"] },
      { id: "bottomcellincolumn", label: "Bottom cell in column", keys: ["Alt-PageDown"] },
      { id: "moverowup", label: "Move row up", keys: ["Alt-Shift-ArrowUp"] },
      { id: "moverowdown", label: "Move row down", keys: ["Alt-Shift-ArrowDown"] },
    ],
  },
];

const BY_ID = {};
KEYBOARD_SHORTCUTS.forEach(({ items }) =>
  items.forEach((item) => {
    BY_ID[item.id] = item;
  })
);

/** "Mod-Shift-z" -> ["Ctrl", "Shift", "Z"] (["⌘", "Shift", "Z"] on a Mac). */
export function shortcutKeys(binding) {
  return binding
    .split("-")
    .map((key) => KEY_LABELS[key] || (key.length === 1 ? key.toUpperCase() : key));
}

/** "Mod-Shift-z" -> "Ctrl+Shift+Z". */
export function formatShortcut(binding) {
  return shortcutKeys(binding).join("+");
}

/**
 * The shortcut to show beside a toolbar button, e.g. "Ctrl+B", or "" if the
 * command has none. Only the first binding: "Redo (Ctrl+Y)", not every
 * alternative.
 */
export function shortcutLabel(id) {
  const item = BY_ID[id];
  if (!item || item.typed) return "";
  return formatShortcut(item.keys[0]);
}
